import Gun from 'gun';
import { installSharingPolicy } from '../../core/sharing-policy';
import { EventEmitter } from 'events';
import { GunBridge, GunPair } from './gun-bridge';
import { RemoteGun, httpGunTransport } from './remote-gun';
import type { CoreRequest } from '../../core/protocol';
import { getSEA } from '../sea-gun';
import { isDevStageZero } from '../dev-stage-env';
import type { User } from '../../shared/types';
import {
  KEY_RECOVERY_WARNINGS,
  toPublicSeaIdentity,
  type DeviceKeyCustodyRecordV1,
  type KeyCustodyRecord,
  type SeaPrivateIdentityMaterial,
  type SeaPublicIdentity,
} from '../../shared/p2p-runtime';
import {
  migrateRecord,
  type SchemaKind,
  type VersionedRecord,
} from '../../shared/p2p-schema-migrations';
import { assertTechSupportDmPair } from '../../shared/techsupport';
import {
  BrowserIdentityCustodyStore,
} from './identity-custody-store';
import {
  IdentityPasswordCustodyManager,
  type IdentityPasswordProtectionStatus,
} from './identity-password-custody-manager';
import {
  BrowserPasswordFreeCustodyManager,
  type PasswordFreeCustodyMigrationSource,
} from './browser-password-free-custody-manager';
import { BrowserPasswordFreeCustodyStore } from './identity-password-free-custody-store';
import { NativePasswordFreeCustodyManager } from './native-password-free-custody-manager';
import { detectNativeCustodyBridge } from '../../shared/native-custody-bridge';

type PasswordFreeCustodyManager = BrowserPasswordFreeCustodyManager | NativePasswordFreeCustodyManager;

export const KEY_CUSTODY_STORAGE = 'iinpublic_key_custody_v1';
/**
 * K3 (docs/TODO.md): distinct from KEY_CUSTODY_STORAGE so the TechSupport
 * device identity never collides with, or gets migrated into, the device's own ordinary
 * identity. Populated only by development/E2E launchers; production bundles reject and erase it.
 * Never populated by the web bundle itself.
 */
export const TECHSUPPORT_KEYPAIR_STORAGE = 'iinpublic_techsupport_keypair_v1';
export const TECHSUPPORT_BROWSER_ROOT_DISABLED_ERROR =
  'TechSupport root browser mode is disabled in production; use a delegated operator identity.';

/** Build-time policy boundary: webpack replaces NODE_ENV, allowing production minification to
 * remove the root-loader branch while dev/E2E can retain it until their fixtures are migrated. */
export function browserTechSupportRootModeAllowed(nodeEnv = process.env.NODE_ENV): boolean {
  return nodeEnv !== 'production';
}

/** Read the development-only root pair. Production erases it before failing closed. */
export function readBrowserTechSupportRootPair(
  storage: Pick<Storage, 'getItem' | 'removeItem'> | undefined,
  nodeEnv = process.env.NODE_ENV,
): GunPair | null {
  const raw = storage?.getItem(TECHSUPPORT_KEYPAIR_STORAGE) ?? null;
  if (!raw) return null;
  if (!browserTechSupportRootModeAllowed(nodeEnv)) {
    storage?.removeItem(TECHSUPPORT_KEYPAIR_STORAGE);
    throw new Error(TECHSUPPORT_BROWSER_ROOT_DISABLED_ERROR);
  }
  const parsed = JSON.parse(raw);
  assertTechSupportDmPair(parsed);
  return parsed as GunPair;
}

/**
 * Pure form of WebGunService's page-origin → Gun-hub-URL derivation, factored
 * out so it's directly unit-testable (the class method only has `window` to
 * read from, which isn't convenient to fake in every test).
 *
 * Convention (dev + e2e parallel workers):
 *   web 3001 ↔ gun 8080   (single-worker default)
 *   web 3002 ↔ gun 8081   (parallel worker 1)
 *   web 3001+N ↔ gun 8080+N, for N in [0, DEV_E2E_WEB_PORT_RANGE_END-3001).
 *
 * LAN dev follows the same mapping when another machine loads the webpack dev
 * server from `https://<dev-host>:3001`: the web origin is on the dev host, but
 * the Gun/API server is still on `https://<dev-host>:8080`. Therefore the
 * dev/e2e port-band check must apply to any hostname, not just localhost.
 *
 * S3 embedded-node (Electron/Android/iOS): the local node serves BOTH the SPA
 * and Gun on the SAME port (e.g. 8088 — see embedded-node-config.ts /
 * platforms/desktop/main.js / NodeForegroundService.kt), so Gun must be
 * derived as same-origin there, NOT via the dev/e2e offset. The port range
 * below is what tells the two apart: dev/e2e/LAN-dev web ports live in a band
 * starting at 3001, while embedded-node ports (8080, 8088, or any operator-
 * chosen port outside that band) fall outside it.
 *
 * The band's upper bound MUST cover `scripts/run-test-all.sh`'s concurrent-wave port
 * scheme: web = 3001 + TEST_ALL_PORT_OFFSET + relativePhaseOffset + workerIndex. The runner
 * reserves 1000 ports for its relative phase bands and workers, and TEST_ALL_PORT_OFFSET lets
 * separate checkouts move that whole reservation without changing code. An earlier fixed
 * bound cut off shifted phases and silently routed them into the same-origin branch below,
 * pointing API/Gun requests at the static web server instead of the paired Gun server.
 */
const DEV_E2E_WEB_PORT_RANGE_START = 3001;
const DEV_E2E_WEB_PORT_RANGE_SIZE = 1000;
const DEV_E2E_GUN_PORT_RANGE_START = 8080;

function configuredTestAllPortOffset(): number {
  const offset = Number(process.env.TEST_ALL_PORT_OFFSET);
  return Number.isFinite(offset) && offset >= 0 ? Math.floor(offset) : 0;
}

function isDevE2EWebPort(webPort: number): boolean {
  const rangeEnd =
    DEV_E2E_WEB_PORT_RANGE_START + configuredTestAllPortOffset() + DEV_E2E_WEB_PORT_RANGE_SIZE;
  return (
    Number.isFinite(webPort) &&
    webPort >= DEV_E2E_WEB_PORT_RANGE_START &&
    webPort < rangeEnd
  );
}

function isLocalHost(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

type BrowserLocationLike = { hostname: string; port: string };
type BrowserStorageLike = Pick<Storage, 'removeItem' | 'getItem'>;

/**
 * Safe ceiling for Gun's own localStorage cache, well under the ~5MB per-origin quota that
 * caused the crash prepareDirectGunBrowserStorage was originally written to fix. Measured in
 * UTF-16 code units (String.prototype.length) — what the quota is actually counted against, not
 * exact bytes, but close enough with this much margin.
 */
const GUN_LOCALSTORAGE_MAX_CHARS = 3 * 1024 * 1024; // ~3MB
const GUN_LOCALSTORAGE_CHECK_INTERVAL_MS = 30_000;

function clearGunLocalStorageIfOversized(storage: BrowserStorageLike | undefined): void {
  try {
    const existing = storage?.getItem?.('gun/');
    if (existing && existing.length > GUN_LOCALSTORAGE_MAX_CHARS) {
      storage?.removeItem('gun/');
    }
  } catch {
    // Gun remains usable in-memory and through the hub/embedded-node peer.
  }
}

export function isEmbeddedNativeOrigin(locationLike: BrowserLocationLike | undefined): boolean {
  if (!locationLike) return false;
  const webPort = Number(locationLike.port);
  return (
    isLocalHost(locationLike.hostname) &&
    Number.isFinite(webPort) &&
    webPort > 0 &&
    !isDevE2EWebPort(webPort)
  );
}

/**
 * Gun's own localStorage cache turned out to be load-bearing, not just a redundant copy of the
 * IndexedDB-backed worker bridge: disabling it everywhere (2026-09-03) stopped
 * `.get().map().on()` from ever firing for newly-written children under a mapped path — including
 * a client's own writes to its own local graph — which silently broke every live multi-user
 * feature built on it (chatroom rosters, presence, and likely more). Confirmed via a 3-user
 * chatroom-roster e2e test: with the cache disabled, a member who just joined never appeared in
 * anyone's roster (stuck at 1 member for the full 15s test timeout, including never seeing their
 * OWN join); re-enabling it, the same test passes immediately.
 *
 * Re-enabled here, but the original 5MB-quota crash that disabling it fixed was real (this
 * adapter serializes and writes the WHOLE graph on every change, with no eviction of its own) —
 * so `clearGunLocalStorageIfOversized` runs here at init AND on a periodic timer
 * (`startGunLocalStorageSizeGuard`, below) to clear the cache outright whenever it grows past a
 * safe ceiling, well before the browser's real quota. The IndexedDB-backed worker bridge
 * (getBridge()) remains the authoritative browser persistence layer regardless, so clearing this
 * cache is never a data-loss risk — Gun's live-update plumbing only needs *some* copy to exist,
 * not any particular copy to survive.
 */
export function prepareDirectGunBrowserStorage(
  _locationLike: BrowserLocationLike | undefined,
  storage: BrowserStorageLike | undefined,
): boolean {
  clearGunLocalStorageIfOversized(storage);
  return true;
}

/**
 * Companion to prepareDirectGunBrowserStorage's init-time check: re-enabling Gun's localStorage
 * cache is only safe long-term with an ongoing size guard, since the adapter itself never evicts.
 * Runs for the life of the page (WebGunService is a page-lifetime singleton; no caller currently
 * needs to stop this early, but the returned function supports that if one ever does).
 */
export function startGunLocalStorageSizeGuard(
  storage: BrowserStorageLike | undefined,
  intervalMs: number = GUN_LOCALSTORAGE_CHECK_INTERVAL_MS,
): () => void {
  if (!storage) return () => {};
  const timer = setInterval(() => clearGunLocalStorageIfOversized(storage), intervalMs);
  return () => clearInterval(timer);
}

export function deriveBackendApiBaseFromLocation(protocol: string, hostname: string, port: string): string {
  const webPort = Number(port);
  if (isDevE2EWebPort(webPort)) {
    const gunPort = webPort - DEV_E2E_WEB_PORT_RANGE_START + DEV_E2E_GUN_PORT_RANGE_START;
    return `${protocol}//${hostname}:${gunPort}`;
  }
  if (isLocalHost(hostname) && Number.isFinite(webPort) && webPort > 0) {
    // Embedded-node (or any other localhost port outside the dev/e2e band): HTTP APIs
    // are mounted on the SAME server that serves this page.
    return `${protocol}//${hostname}:${webPort}`;
  }
  if (isLocalHost(hostname)) {
    return `${protocol}//${hostname}:${DEV_E2E_GUN_PORT_RANGE_START}`;
  }
  return `${protocol}//${hostname}${port ? `:${port}` : ''}`;
}

export function deriveGunHubUrlFromLocation(protocol: string, hostname: string, port: string): string {
  const webPort = Number(port);
  if (isDevE2EWebPort(webPort)) {
    const gunPort = webPort - DEV_E2E_WEB_PORT_RANGE_START + DEV_E2E_GUN_PORT_RANGE_START;
    return `${protocol}//${hostname}:${gunPort}/gun`;
  }
  if (isLocalHost(hostname) && Number.isFinite(webPort) && webPort > 0) {
    // Embedded-node (or any other localhost port outside the dev/e2e band): Gun is
    // mounted on the SAME http server that serves this page — same-origin, no offset.
    return `${protocol}//${hostname}:${webPort}/gun`;
  }
  if (isLocalHost(hostname)) {
    return `${protocol}//${hostname}:${DEV_E2E_GUN_PORT_RANGE_START}/gun`;
  }
  return `${deriveBackendApiBaseFromLocation(protocol, hostname, port)}/gun`;
}
export const KEY_CUSTODY_DEVICE_SECRET_STORAGE = 'iinpublic_key_custody_device_secret_v1';
const KEY_CUSTODY_ITERATIONS = 150_000;

export class IdentityPasswordRequiredError extends Error {
  constructor(public readonly publicIdentity: SeaPublicIdentity) {
    super('Identity password is required');
    this.name = 'IdentityPasswordRequiredError';
  }
}

/**
 * WebGunService — dual-mode Gun service.
 *
 * EXISTING API: the direct Gun instance is kept intact so existing services
 * (chatroom, talk, conversation) can continue to call `getGun()` without change.
 *
 * NEW API: a GunBridge worker is created alongside the direct instance, exposing
 * the Web-Worker-based path (IndexedDB persistence, SEA identity, private
 * encrypted space, flexible graph) via dedicated methods.  New features should
 * use `getBridge()` or the typed helpers on this class.
 */
export class WebGunService extends EventEmitter {
  private gun: any;
  private bridge: GunBridge;
  private bridgeReady: boolean = false;
  private peers: string[];
  private connected: boolean = false;
  /** In-memory copy of the SEA pair after `ensureKeypairAndAuth()`. */
  private seaPair: GunPair | null = null;
  /** Serialize authenticated private-namespace writes; GUN/SEA can reject overlapping signs. */
  private privateWriteQueue: Promise<void> = Promise.resolve();
  private identityPasswordManager: IdentityPasswordCustodyManager | null = null;
  private browserPasswordFreeCustodyManager: PasswordFreeCustodyManager | null | undefined;
  /** Cached from the last `getIdentityPasswordProtectionStatus()` read (and kept current by
   * `setIdentityPassword`/`removeIdentityPassword`/`unlockIdentity`) so the synchronous
   * `beforeunload` handler can tell, without awaiting anything, whether there is a
   * password-protected identity worth clearing from memory before an unload. */
  private identityPasswordActive = false;

  constructor() {
    super();
    this.peers = [WebGunService.deriveGunHubUrl()];
    this.bridge = new GunBridge('/core-worker.js');
  }

  private isE2ERelaxedMode(): boolean {
    return process.env.DISABLE_HMR === 'true';
  }

  private isEmbeddedLocalOrigin(): boolean {
    return isEmbeddedNativeOrigin(
      typeof window !== 'undefined' && window.location ? window.location : undefined,
    );
  }

  private getIdentityPasswordManager(): IdentityPasswordCustodyManager | null {
    if (typeof indexedDB === 'undefined') return null;
    if (this.identityPasswordManager) return this.identityPasswordManager;
    const store = new BrowserIdentityCustodyStore();
    const legacyCustody = this.getBrowserPasswordFreeCustodyManager() ?? this.getLegacyPasswordFreeCustody();
    this.identityPasswordManager = new IdentityPasswordCustodyManager(store, legacyCustody, {
      legacyRecordStorageKey: KEY_CUSTODY_STORAGE,
      legacySecretStorageKey: KEY_CUSTODY_DEVICE_SECRET_STORAGE,
    });
    return this.identityPasswordManager;
  }

  private getLegacyPasswordFreeCustody(): PasswordFreeCustodyMigrationSource & {
    assertMatches(expected: SeaPublicIdentity): Promise<void>;
    writeAndVerify(pair: SeaPrivateIdentityMaterial): Promise<void>;
    assertPairMatches(pair: SeaPrivateIdentityMaterial): Promise<void>;
    clear(): Promise<void>;
  } {
    return {
      assertMatches: async (expected) => {
        const record = this.readCustodyRecord();
        if (!record || record.version !== 1) throw new Error('Password-free identity custody is unavailable');
        const pair = await this.unwrapKeypairFromStorage(record);
        if (!pair) throw new Error('Password-free identity custody could not be verified');
        const actual = toPublicSeaIdentity(pair as SeaPrivateIdentityMaterial);
        if (actual.pub !== expected.pub || actual.epub !== expected.epub) {
          throw new Error('Password-free identity custody does not match the active identity');
        }
      },
      removeIfMatches: async (expected) => {
        const record = this.readCustodyRecord();
        if (!record) return;
        if (
          record.version !== 1 ||
          record.publicIdentity.pub !== expected.pub ||
          record.publicIdentity.epub !== expected.epub
        ) {
          throw new Error('Refusing to remove custody for a different identity');
        }
        const pair = await this.unwrapKeypairFromStorage(record);
        if (pair) {
          const actual = toPublicSeaIdentity(pair as SeaPrivateIdentityMaterial);
          if (actual.pub !== expected.pub || actual.epub !== expected.epub) {
            throw new Error('Refusing to remove custody for a different identity');
          }
        }
        localStorage.removeItem(KEY_CUSTODY_STORAGE);
        localStorage.removeItem(KEY_CUSTODY_DEVICE_SECRET_STORAGE);
      },
      writeAndVerify: async (expectedPair) => {
        localStorage.removeItem(KEY_CUSTODY_STORAGE);
        localStorage.removeItem(KEY_CUSTODY_DEVICE_SECRET_STORAGE);
        await this.persistCustodyRecord(expectedPair as GunPair);
        const committed = this.readCustodyRecord();
        const actualPair = committed ? await this.unwrapKeypairFromStorage(committed) : null;
        if (
          !actualPair ||
          actualPair.pub !== expectedPair.pub ||
          actualPair.epub !== expectedPair.epub ||
          actualPair.priv !== expectedPair.priv ||
          actualPair.epriv !== expectedPair.epriv
        ) {
          throw new Error('Password-free identity custody mismatch');
        }
      },
      assertPairMatches: async (expectedPair) => {
        const record = this.readCustodyRecord();
        const actualPair = record ? await this.unwrapKeypairFromStorage(record) : null;
        if (
          !actualPair ||
          actualPair.pub !== expectedPair.pub ||
          actualPair.epub !== expectedPair.epub ||
          actualPair.priv !== expectedPair.priv ||
          actualPair.epriv !== expectedPair.epriv
        ) {
          throw new Error('Password-free identity custody mismatch');
        }
      },
      clear: async () => {
        localStorage.removeItem(KEY_CUSTODY_STORAGE);
        localStorage.removeItem(KEY_CUSTODY_DEVICE_SECRET_STORAGE);
      },
      readPair: async () => {
        const record = this.readCustodyRecord();
        return record ? await this.unwrapKeypairFromStorage(record) as SeaPrivateIdentityMaterial | null : null;
      },
    };
  }

  /**
   * Browser v3 deliberately stays disabled inside native shells until those shells expose a
   * reviewed OS Keychain/Keystore bridge. An Electron/WebView IndexedDB CryptoKey is not an OS
   * keystore and must not be presented as one.
   */
  private getBrowserPasswordFreeCustodyManager(): PasswordFreeCustodyManager | null {
    if (this.browserPasswordFreeCustodyManager !== undefined) {
      return this.browserPasswordFreeCustodyManager;
    }
    const nativeBridge = detectNativeCustodyBridge();
    if (nativeBridge) {
      this.browserPasswordFreeCustodyManager = new NativePasswordFreeCustodyManager(nativeBridge);
      return this.browserPasswordFreeCustodyManager;
    }
    if (
      this.isEmbeddedLocalOrigin() ||
      typeof indexedDB === 'undefined' ||
      !this.getBrowserCrypto()
    ) {
      this.browserPasswordFreeCustodyManager = null;
      return null;
    }
    try {
      this.browserPasswordFreeCustodyManager = new BrowserPasswordFreeCustodyManager(
        new BrowserPasswordFreeCustodyStore(),
      );
    } catch {
      this.browserPasswordFreeCustodyManager = null;
    }
    return this.browserPasswordFreeCustodyManager;
  }

  /**
   * Compute the Gun hub URL from the current page origin.
   *
   * When running outside a browser (SSR / unit test) falls back to the legacy default so
   * existing callers that construct this class in Node don't break.
   */
  private static deriveGunHubUrl(): string {
    if (typeof window === 'undefined' || !window.location) {
      // Node/SSR fallback: honour PORT env var so callers running inside a parallel
      // worker process (web 3001+N ↔ gun 8080+N) still target their own Gun server.
      const envPort = typeof process !== 'undefined' && process.env && process.env.PORT
        ? parseInt(process.env.PORT, 10)
        : 8080;
      return `https://localhost:${Number.isFinite(envPort) ? envPort : 8080}/gun`;
    }
    const { protocol, hostname, port } = window.location;
    return deriveGunHubUrlFromLocation(protocol, hostname, port);
  }

  /**
   * Native (OPEN-91 P5): the app's embedded node holds the device's one Gun and its core. The
   * WebView then runs no Gun — `getGun()` is a thin client of the node's (`remote-gun.ts`) — and no
   * worker, unless its old data still has to be copied to the node (`keepWorker`).
   */
  private nativeNode: { token: string; baseUrl: string; core: (request: CoreRequest) => Promise<any>; keepWorker: boolean } | null = null;
  private readonly privateSubscribers = new Map<string, Set<(data: any) => void>>();

  useNativeNode(node: { token: string; baseUrl: string; core: (request: CoreRequest) => Promise<any>; keepWorker: boolean }): void {
    this.nativeNode = node;
  }

  isNativeNode(): boolean {
    return this.nativeNode !== null;
  }

  async initialize(): Promise<void> {
    if (this.nativeNode) {
      this.gun = new RemoteGun(httpGunTransport(this.nativeNode.baseUrl, this.nativeNode.token));
      this.connected = true;
      console.log('🔗 Gun: the app node’s (no Gun in the WebView)');
      if (this.nativeNode.keepWorker) {
        // Only to copy what an earlier version kept in the WebView over to the node.
        try {
          await this.bridge.init({ hubUrl: this.peers[0], isolated: true });
          this.bridgeReady = true;
        } catch (bridgeErr) {
          this.bridgeReady = false;
          console.warn('⚠️ WebView worker unavailable — nothing to copy from it:', bridgeErr);
        }
      }
      return;
    }
    // The page's Gun is the core worker's (OPEN-91 P6): one Gun on the device, persisted to
    // IndexedDB and peered with the hub behind the sharing policy. The page keeps Gun's chain API
    // through a thin client. Failure must not start another independent database writer.
    if (this.workerGunAllowed()) {
      try {
        await this.bridge.init({ hubUrl: this.peers[0], peer: true });
        this.bridgeReady = true;
        this.gun = new RemoteGun(this.bridge.gunTransport());
        this.gun.on('hi', () => console.log('🤝 Gun peer connected (core worker)'));
        this.connected = true;
        console.log('🔗 Gun: the core worker’s (no Gun on the page) — hub:', this.peers[0]);
        return;
      } catch (bridgeErr) {
        this.bridge.terminate();
        throw new Error('Device storage could not start safely. Reload this page to retry.', { cause: bridgeErr });
      }
    }
    await this.initializePageGun();
  }

  /** `?pageGun=1` keeps the pre-P6 layout (Gun on the page, a peerless worker beside it), for comparison. */
  private workerGunAllowed(): boolean {
    try {
      return new URLSearchParams(window.location.search).get('pageGun') !== '1';
    } catch {
      return true;
    }
  }

  private async initializePageGun(): Promise<void> {
    try {
      // ── Direct Gun instance (backward compat for existing services) ──
      // Gun AXE (axe.js) auto-adds localhost peers and can mesh multiple parallel e2e
      // webpack+Gun servers (3001/3002 ↔ 8080/8081), splitting graphs so headcounts stay at 1.
      // E2E bundles set DISABLE_HMR via webpack DefinePlugin — turn AXE off there only.
      // Do not gate on `typeof process` / `process.env`: webpack 5 browser bundles often have no
      // `process`, so the guard made e2eDisableAxe always false and AXE never disabled.
      const e2eDisableAxe = this.isE2ERelaxedMode();
      const devStageZero = isDevStageZero();
      const disableAxe = e2eDisableAxe || devStageZero;
      const persistBrowserGraph = prepareDirectGunBrowserStorage(
        typeof window !== 'undefined' && window.location ? window.location : undefined,
        typeof localStorage !== 'undefined' ? localStorage : undefined,
      );
      // Private namespaces never reach the hub, whatever writes them (OPEN-91 P2).
      installSharingPolicy(Gun);
      this.gun = Gun({
        peers: this.peers,
        sharingRelayPeers: [...this.peers],
        localStorage: persistBrowserGraph,
        radisk: false,
        ...(disableAxe ? { axe: false, multicast: false } : {}),
      });
      if (persistBrowserGraph) {
        startGunLocalStorageSizeGuard(typeof localStorage !== 'undefined' ? localStorage : undefined);
      }

      this.gun.on('hi', (peer: any) => {
        console.log('🤝 Gun peer connected:', peer.id || 'unknown');
      });
      this.gun.on('bye', (peer: any) => {
        console.log('👋 Gun peer disconnected:', peer.id || 'unknown');
      });

      // ── Worker bridge (new, IndexedDB-backed, SEA-enabled) ──
      // Non-fatal: if the bridge worker fails (e.g. script load error), the app
      // continues using the direct Gun instance for all existing functionality.
      this.connected = true;
      console.log('🔗 Gun.js initialized — hub:', this.peers[0]);

      // The direct Gun instance is usable immediately from its local graph. Hub presence is a
      // hydration concern, not a reason to hold startup for 5–12 seconds on a phone. Existing
      // put/get operations retain their own bounded ACK waits.
      void this.waitForHubPeer(disableAxe ? 12_000 : 5000);
      try {
        await this.bridge.init({ hubUrl: this.peers[0] });
        this.bridgeReady = true;
        console.log('🔗 Gun.js worker bridge ready (IndexedDB-backed via worker)');
      } catch (bridgeErr) {
        this.bridgeReady = false;
        console.warn('⚠️ Gun worker bridge unavailable — SEA/IndexedDB features disabled:', bridgeErr);
      }
    } catch (error) {
      console.error('Failed to initialize Gun.js service:', error);
      throw error;
    }
  }

  /** Direct Gun instance — used by existing services (chatroom, talk, etc.). */
  getGun(): any {
    return this.gun;
  }

  /**
   * Add another phone's embedded node as a Gun peer (OPEN-36 offline mode: LAN or Wi-Fi Direct).
   * The caller validates the URL. A Wi-Fi Direct group is announced before its route carries
   * traffic, and a first failed connect put Gun into a long reconnect backoff (51 s on hardware
   * before the group link came up), so probe with a plain WebSocket every second and hand the
   * peer to Gun only once it answers. Gun then keeps retrying a dropped peer on its own.
   */
  addPeer(url: string, probe: { attempts?: number; intervalMs?: number } = {}): void {
    if (!this.gun || !url) return;
    if (this.gun._?.opt?.peers?.[url] || this.pendingPeerProbes.has(url)) return;
    const attempts = probe.attempts ?? 60;
    const intervalMs = probe.intervalMs ?? 1_000;
    if (typeof WebSocket === 'undefined' || attempts <= 0) {
      this.gun.opt({ peers: [url] });
      return;
    }
    this.pendingPeerProbes.add(url);
    const wsUrl = url.replace(/^http/, 'ws');
    const startedAt = Date.now();
    let tries = 0;
    const attempt = (): void => {
      tries += 1;
      let settled = false;
      const done = (ok: boolean): void => {
        if (settled) return;
        settled = true;
        try { socket.close(); } catch { /* already closed */ }
        if (ok || tries >= attempts) {
          this.pendingPeerProbes.delete(url);
          console.info(`[gun-peer] ${url} ${ok ? 'answered' : 'unanswered'} after ${tries} probe(s), ${Date.now() - startedAt} ms`);
          this.gun.opt({ peers: [url] });
          if (ok) this.emit('peer-added', url);
        } else {
          setTimeout(attempt, intervalMs);
        }
      };
      const socket = new WebSocket(wsUrl);
      socket.onopen = () => done(true);
      socket.onerror = () => done(false);
      setTimeout(() => done(false), Math.max(500, intervalMs * 2));
    };
    attempt();
  }

  private readonly pendingPeerProbes = new Set<string>();

  /**
   * Worker-backed GunBridge — use for new features:
   * SEA identity, IndexedDB persistence, private encrypted space, graph nodes.
   */
  getBridge(): GunBridge {
    return this.bridge;
  }

  /** Whether the worker bridge finished initializing (IndexedDB-backed Gun instance is up). */
  isBridgeReady(): boolean {
    return this.bridgeReady;
  }

  /* ── Serialization helpers ─────────────────────────────────────── */

  private serializeDates(obj: any): any {
    if (obj === undefined || obj === null) return null;
    if (obj instanceof Date) return obj.toISOString();
    if (Array.isArray(obj)) {
      const arrayObj: any = { _isArray: true, _length: obj.length, isArray: true, length: obj.length };
      obj.forEach((item, index) => {
        const serialized = this.serializeDates(item);
        if (serialized !== null && serialized !== undefined) {
          arrayObj[index.toString()] = serialized;
        }
      });
      return arrayObj;
    }
    if (obj && typeof obj === 'object') {
      const serialized: any = {};
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          const value = this.serializeDates(obj[key]);
          if (value !== undefined && value !== null) {
            serialized[key] = value;
          }
        }
      }
      return serialized;
    }
    return obj;
  }

  private deserializeDates(obj: any): any {
    if (typeof obj === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(obj)) {
      return new Date(obj);
    }
    if (obj && typeof obj === 'object' && (obj._isArray || obj.isArray)) {
      const result: any[] = [];
      const length = obj._length || obj.length || 0;
      for (let i = 0; i < length; i++) {
        if (Object.prototype.hasOwnProperty.call(obj, i.toString())) {
          result[i] = this.deserializeDates(obj[i.toString()]);
        }
      }
      return result;
    }
    if (obj && typeof obj === 'object') {
      const deserialized: any = {};
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          deserialized[key] = this.deserializeDates(obj[key]);
        }
      }
      return deserialized;
    }
    return obj;
  }

  private bytesToBase64(bytes: Uint8Array): string {
    let binary = '';
    bytes.forEach((byte) => {
      binary += String.fromCharCode(byte);
    });
    return btoa(binary);
  }

  private base64ToBytes(value: string): Uint8Array {
    const binary = atob(value);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  private bytesToArrayBuffer(bytes: Uint8Array): ArrayBuffer {
    return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
  }

  private getBrowserCrypto(): Crypto | null {
    return typeof globalThis !== 'undefined' && globalThis.crypto?.subtle ? globalThis.crypto : null;
  }

  private getOrCreateDeviceSecret(): string {
    if (typeof localStorage === 'undefined') {
      throw new Error('Browser localStorage is required for device-key custody');
    }
    const existing = localStorage.getItem(KEY_CUSTODY_DEVICE_SECRET_STORAGE);
    if (existing) return existing;
    const crypto = this.getBrowserCrypto();
    const bytes = new Uint8Array(32);
    if (crypto?.getRandomValues) crypto.getRandomValues(bytes);
    else {
      for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
    }
    const secret = this.bytesToBase64(bytes);
    localStorage.setItem(KEY_CUSTODY_DEVICE_SECRET_STORAGE, secret);
    return secret;
  }

  private async deriveCustodyKey(secret: string, salt: Uint8Array, iterations: number): Promise<CryptoKey> {
    const crypto = this.getBrowserCrypto();
    if (!crypto) {
      throw new Error('WebCrypto is required for encrypted SEA key custody');
    }
    const keyMaterial = await crypto.subtle.importKey(
      'raw',
      new TextEncoder().encode(secret),
      'PBKDF2',
      false,
      ['deriveKey'],
    );
    return crypto.subtle.deriveKey(
      { name: 'PBKDF2', salt: this.bytesToArrayBuffer(salt), iterations, hash: 'SHA-256' },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt'],
    );
  }

  private async wrapKeypairForStorage(
    pair: GunPair,
    existing?: KeyCustodyRecord | null,
  ): Promise<DeviceKeyCustodyRecordV1> {
    const crypto = this.getBrowserCrypto();
    if (!crypto) {
      throw new Error('WebCrypto is required for encrypted SEA key custody');
    }
    const now = new Date().toISOString();
    const salt = new Uint8Array(16);
    const iv = new Uint8Array(12);
    crypto.getRandomValues(salt);
    crypto.getRandomValues(iv);
    const secret = this.getOrCreateDeviceSecret();
    const key = await this.deriveCustodyKey(secret, salt, KEY_CUSTODY_ITERATIONS);
    const plaintext = new TextEncoder().encode(JSON.stringify(pair));
    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: this.bytesToArrayBuffer(iv) },
      key,
      this.bytesToArrayBuffer(plaintext),
    );
    return {
      version: 1,
      format: 'webcrypto-device-key-v1',
      publicIdentity: toPublicSeaIdentity(pair as SeaPrivateIdentityMaterial),
      wrapping: {
        kdf: 'PBKDF2-SHA256',
        iterations: KEY_CUSTODY_ITERATIONS,
        salt: this.bytesToBase64(salt),
        iv: this.bytesToBase64(iv),
      },
      ciphertext: this.bytesToBase64(new Uint8Array(encrypted)),
      createdAt: existing?.createdAt || now,
      updatedAt: now,
    };
  }

  private async unwrapKeypairFromStorage(record: KeyCustodyRecord): Promise<GunPair | null> {
    try {
      if (record.version !== 1 || record.format !== 'webcrypto-device-key-v1') return null;
      const crypto = this.getBrowserCrypto();
      if (!crypto) return null;
      const secret = this.getOrCreateDeviceSecret();
      const salt = this.base64ToBytes(record.wrapping.salt);
      const iv = this.base64ToBytes(record.wrapping.iv);
      const key = await this.deriveCustodyKey(secret, salt, record.wrapping.iterations);
      const decrypted = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: this.bytesToArrayBuffer(iv) },
        key,
        this.bytesToArrayBuffer(this.base64ToBytes(record.ciphertext)),
      );
      const pair = JSON.parse(new TextDecoder().decode(decrypted)) as GunPair;
      if (!pair?.pub || !pair?.epub || !pair?.priv || !pair?.epriv) return null;
      return pair;
    } catch {
      return null;
    }
  }

  private readCustodyRecord(): KeyCustodyRecord | null {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(KEY_CUSTODY_STORAGE);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as KeyCustodyRecord;
    } catch {
      return null;
    }
  }

  private async persistCustodyRecord(pair: GunPair, existing?: KeyCustodyRecord | null): Promise<void> {
    if (typeof localStorage === 'undefined') return;
    const record = await this.wrapKeypairForStorage(pair, existing);
    localStorage.setItem(KEY_CUSTODY_STORAGE, JSON.stringify(record));
  }

  /* ── Core graph operations (direct Gun — backward compat) ──────── */

  /** Wait until the relay hub accepts connections (avoids puts racing server boot / graph wipe). */
  private waitForHubPeer(maxMs: number): Promise<void> {
    const gun = this.gun;
    if (!gun) return Promise.resolve();
    return new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      gun.on('hi', finish);
      setTimeout(finish, maxMs);
    });
  }

  async put(key: string, data: any): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const serializedData = this.serializeDates(data);
        const longerBudget = isDevStageZero() || this.isE2ERelaxedMode();
        let settled = false;
        const done = (err?: Error) => {
          if (settled) return;
          settled = true;
          clearTimeout(timeout);
          if (err) reject(err);
          else resolve();
        };

        // No ack within the budget means we don't know whether the relay stored it — not that
        // it failed. Gun is eventually-consistent and the local in-memory graph already has the
        // write; treating a bare timeout as fatal here strands boot-path callers (e.g. a brand
        // new user's first `users/<id>` put) on "Failed to initialize the app" whenever the
        // relay's own ack is slow or absent (see the relay's radisk:false write-ack gaps in
        // project memory). An explicit ack.err below still rejects — only the "no answer at all"
        // case is treated as optimistic-continue, in every environment, not just dev/e2e.
        // Short budget, matching gun-service.ts's own 300/500ms "fast" server-side convention for
        // the same unreliable-ack problem: production runs `RELAY_ONLY_HUB` (no application
        // radata — see p2p-runtime.ts), so a lone browser's put NEVER gets a real ack from the
        // relay itself, only from a genuine second peer if one is meshed in. Waiting multiple
        // seconds here was pure dead time on every boot, not a real chance at a slow-but-real ack.
        const timeout = setTimeout(() => {
          console.warn(`Gun put ack timed out, continuing optimistically: ${key}`);
          done();
        }, longerBudget ? 2000 : 800);

        this.gun.get(key).put(serializedData, (ack: any) => {
          if (ack?.err) {
            done(new Error(String(ack.err)));
          } else {
            console.log('✅ Gun put success:', key);
            done();
          }
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  async get(key: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const off = this.gun.get(key).once((data: any) => {
        clearTimeout(timeout);
        if (data === undefined) {
          reject(new Error(`No data found for key: ${key}`));
        } else {
          resolve(this.deserializeDates(data));
        }
      });

      const timeout = setTimeout(() => {
        off.off();
        reject(new Error(`Timeout getting data for key: ${key}`));
      }, 8000);
    });
  }

  private async getPublicUserFromApi(userId: string): Promise<Partial<User> | null> {
    if (typeof window !== 'undefined' && window.location) {
      try {
        const { protocol, hostname, port } = window.location;
        const apiBase = deriveBackendApiBaseFromLocation(protocol, hostname, port);
        const response = await fetch(`${apiBase}/api/users/${encodeURIComponent(userId)}`, {
          cache: 'no-store',
        });
        if (response.ok) {
          const fromApi = (await response.json()) as Partial<User>;
          if (fromApi?.id) return fromApi;
        }
      } catch {
        // Fall through to the Gun graph.
      }
    }
    return null;
  }

  /**
   * Chatroom-roster epub cache. The membership heartbeat writes each member's epub inline
   * into `chatrooms/<id>/users/<uid>` (web-chatroom-service), which syncs reliably even when
   * the `users/<id>` public record / presence lookup is racing on simultaneous boot. The app
   * harvests those epubs here so getPublicUser can fill a missing epub from the roster instead
   * of leaving received DMs stuck as raw SEA ciphertext.
   */
  private readonly rosterEpubByUserId = new Map<string, string>();

  rememberRosterEpub(userId: string, epub: string): void {
    const id = String(userId || '').trim();
    const key = String(epub || '').trim();
    if (id && key) this.rosterEpubByUserId.set(id, key);
  }

  /** Read the public user graph record stored at `users/<id>`. */
  async getPublicUser(userId: string): Promise<Partial<User>> {
    const rosterEpub = this.rosterEpubByUserId.get(userId);
    const withRosterEpub = (record: Partial<User>): Partial<User> =>
      !record.epub && rosterEpub ? { ...record, epub: rosterEpub } : record;

    const fromApi = await this.getPublicUserFromApi(userId);
    if (fromApi?.pub && fromApi?.epub) return fromApi;

    let fromGun: Partial<User> | null = null;
    try {
      fromGun = await this.get(`users/${userId}`) as Partial<User>;
      if (fromGun?.pub && fromGun?.epub) return { ...(fromApi || {}), ...fromGun };
    } catch {
      fromGun = null;
    }

    if (fromApi) return withRosterEpub({ ...(fromGun || {}), ...fromApi });
    if (fromGun) return withRosterEpub(fromGun);
    if (rosterEpub) return { id: userId, epub: rosterEpub };
    throw new Error(`No data found for key: users/${userId}`);
  }

  subscribe(key: string, callback: (data: any) => void): () => void {
    const off = this.gun.get(key).on((data: any) => {
      callback(data);
      this.emit('newMessage', data);
    });
    return () => off.off();
  }

  /* ── Identity (SEA) — routed through the worker bridge ─────────── */

  createAccount(): Promise<{ pub: string; epub: string }> {
    return this.bridge.createAccount();
  }

  login(pair: GunPair): Promise<{ pub: string; epub: string }> {
    return this.bridge.login(pair);
  }

  logout(): Promise<void> {
    return this.bridge.logout();
  }

  /** Write to the current user's public namespace (readable by all). */
  putPublic(key: string, data: any): Promise<void> {
    if (this.nativeNode) {
      const pub = this.seaPair?.pub || 'anonymous';
      return this.nativeNode.core({ name: 'device.put', path: `public/${pub}/${key}`, value: this.serializeDates(data) }).then(() => undefined);
    }
    return this.bridge.putPublic(key, this.serializeDates(data));
  }

  /** Write to the current user's private namespace (AES-encrypted, owner-only). */
  async putPrivate(key: string, data: any): Promise<void> {
    const write = this.privateWriteQueue
      .catch(() => undefined)
      .then(() => this.putPrivateWithRetry(key, data));
    this.privateWriteQueue = write;
    return write;
  }

  private async putPrivateWithRetry(key: string, data: any): Promise<void> {
    const maxAttempts = 3;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        await this.putPrivateOnce(key, data);
        return;
      } catch (error) {
        const transientSeaConflict = String((error as Error)?.message || error).includes('Unverified data');
        if (!transientSeaConflict || attempt === maxAttempts) throw error;
        await new Promise((resolve) => setTimeout(resolve, 25 * attempt));
      }
    }
  }

  private async putPrivateOnce(key: string, data: any): Promise<void> {
    if (!this.seaPair) {
      throw new Error('SEA keypair not authenticated');
    }
    if (this.nativeNode) {
      // Encrypted here with the owner's keypair, as the worker did; the node stores only ciphertext.
      const serialized = this.serializeDates(data);
      const ciphertext = await getSEA().encrypt(JSON.stringify(serialized), this.seaPair);
      await this.nativeNode.core({ name: 'private.put', key, value: ciphertext });
      const decoded = this.deserializeDates(JSON.parse(JSON.stringify(serialized)));
      for (const listener of this.privateSubscribers.get(key) ?? []) {
        try { listener(decoded); } catch { /* one subscriber must not stop the others */ }
      }
      return;
    }
    if (!this.bridgeReady) {
      throw new Error('Local private Gun store is unavailable');
    }

    // Private owner data belongs in the worker's local-only, IndexedDB-backed Gun instance.
    // The main-thread Gun is peered for public control/discovery and must never receive new
    // private bodies, even as ciphertext: Gun peers may replicate any node they can see.
    await this.bridge.putPrivate(key, this.serializeDates(data));
  }

  /** A brand-new identity: everything it will ever own lives in the local private store. */
  private async createFreshPair(): Promise<GunPair> {
    return await getSEA().pair() as GunPair;
  }

  /** Read and decrypt from the current user's private namespace. */
  async getPrivate(key: string): Promise<any> {
    if (!this.seaPair) {
      throw new Error('SEA keypair not authenticated');
    }
    if (this.nativeNode) {
      const ciphertext = await this.nativeNode.core({ name: 'private.get', key });
      if (!ciphertext) return null;
      const decrypted = await getSEA().decrypt(ciphertext, this.seaPair);
      if (decrypted === undefined || decrypted === null) return null;
      return this.deserializeDates(typeof decrypted === 'string' ? JSON.parse(decrypted) : decrypted);
    }
    if (!this.bridgeReady) {
      throw new Error('Local private Gun store is unavailable');
    }

    const local = await this.bridge.getPrivate(key);
    return local === null || local === undefined ? null : this.deserializeDates(local);
  }

  /** Subscribe to decrypted owner-private data in the local-only worker Gun. */
  subscribePrivate(key: string, callback: (data: any) => void): () => void {
    if (!this.seaPair) throw new Error('SEA keypair not authenticated');
    if (this.nativeNode) {
      // The WebView is the only writer of its owner's private records: its own writes notify.
      let listeners = this.privateSubscribers.get(key);
      if (!listeners) this.privateSubscribers.set(key, (listeners = new Set()));
      listeners.add(callback);
      void this.getPrivate(key).then((value) => { if (value !== null && listeners!.has(callback)) callback(value); }).catch(() => undefined);
      return () => { listeners!.delete(callback); };
    }
    if (!this.bridgeReady) throw new Error('Local private Gun store is unavailable');
    return this.bridge.subscribePrivate(key, (data) => callback(this.deserializeDates(data)));
  }

  /* ── Flexible Graph schema ─────────────────────────────────────── */

  /** Create a named node in the shared graph. */
  createNode(nodeId: string, nodeData: Record<string, any>): Promise<{ nodeId: string }> {
    return this.bridge.createNode(nodeId, this.serializeDates(nodeData));
  }

  /**
   * Create a directed edge between two nodes.
   * Stored at: edges/<fromId>/<edgeLabel>/<toId>
   */
  linkNodes(fromId: string, toId: string, edgeLabel = 'link'): Promise<void> {
    return this.bridge.linkNodes(fromId, toId, edgeLabel);
  }

  isConnected(): boolean {
    return this.connected;
  }

  async getIdentityPasswordProtectionStatus(): Promise<IdentityPasswordProtectionStatus> {
    if (
      browserTechSupportRootModeAllowed() &&
      typeof localStorage !== 'undefined' &&
      localStorage.getItem(TECHSUPPORT_KEYPAIR_STORAGE)
    ) {
      this.identityPasswordActive = false;
      return { state: 'not-set' };
    }
    const manager = this.getIdentityPasswordManager();
    const status = manager ? await manager.getStatus() : ({ state: 'not-set' } as const);
    this.identityPasswordActive = status.state === 'locked';
    return status;
  }

  /** Synchronous best-known answer to "does this identity have password protection
   * configured" — for call sites (like `beforeunload`) that cannot await `getIdentityPasswordProtectionStatus()`. */
  isIdentityPasswordActive(): boolean {
    return this.identityPasswordActive;
  }

  async setIdentityPassword(password: string): Promise<void> {
    const pair = this.seaPair;
    if (!pair) throw new Error('Identity must be unlocked before setting a password');
    const manager = this.getIdentityPasswordManager();
    if (!manager) throw new Error('Password protection is unavailable on this platform');
    await manager.setPassword(pair as SeaPrivateIdentityMaterial, password);
    this.identityPasswordActive = true;
  }

  async changeIdentityPassword(currentPassword: string, newPassword: string): Promise<void> {
    if (!this.seaPair) throw new Error('Identity must be unlocked before changing its password');
    const manager = this.getIdentityPasswordManager();
    if (!manager) throw new Error('Password protection is unavailable on this platform');
    const record = await manager.changePassword(currentPassword, newPassword);
    const current = this.seaPair;
    if (
      !current ||
      record.publicIdentity.pub !== current.pub ||
      record.publicIdentity.epub !== current.epub
    ) {
      throw new Error('Password change returned a different identity');
    }
  }

  async removeIdentityPassword(currentPassword: string): Promise<void> {
    const current = this.seaPair;
    if (!current) throw new Error('Identity must be unlocked before removing its password');
    const manager = this.getIdentityPasswordManager();
    if (!manager) throw new Error('Password protection is unavailable on this platform');
    const pair = await manager.removePassword(currentPassword);
    if (
      pair.pub !== current.pub ||
      pair.epub !== current.epub ||
      pair.priv !== current.priv ||
      pair.epriv !== current.epriv
    ) {
      throw new Error('Password removal returned a different identity');
    }
    this.identityPasswordActive = false;
  }

  async unlockIdentity(password: string): Promise<GunPair> {
    const manager = this.getIdentityPasswordManager();
    if (!manager) throw new Error('Password protection is unavailable on this platform');
    const pair = await manager.unlock(password);
    await this.authenticatePair(pair);
    this.identityPasswordActive = true;
    return pair;
  }

  async lockIdentityNow(): Promise<void> {
    this.seaPair = null;
    this.privateWriteQueue = Promise.resolve();
    try {
      this.gun?.user?.().leave?.();
    } catch {
      // The in-memory pair is already gone; Gun logout is best effort.
    }
    if (this.bridgeReady) {
      await this.bridge.logout().catch(() => undefined);
    }
  }

  private async authenticatePair(pair: GunPair): Promise<void> {
    if (this.isEmbeddedLocalOrigin()) {
      console.warn('⚠️ Skipping blocking Gun user auth during embedded-node startup; using local SEA pair');
    } else {
      const gun = this.gun;
      console.log('🔐 Authenticating local SEA identity');
      await new Promise<void>((resolve, reject) => {
        let settled = false;
        const finish = (error?: Error) => {
          if (settled) return;
          settled = true;
          clearTimeout(timeout);
          if (error) reject(error);
          else resolve();
        };
        const timeout = setTimeout(() => {
          console.warn('⚠️ Gun SEA auth ack timed out — continuing with in-memory SEA pair');
          finish();
        }, 5000);
        gun.user().auth(pair, (ack: any) => {
          if (ack && ack.err) finish(new Error(String(ack.err)));
          else finish();
        });
      });
    }

    if (this.bridgeReady) {
      try {
        await this.bridge.login(pair);
      } catch (error) {
        console.warn('⚠️ Gun worker bridge login unavailable — private SEA helpers disabled:', error);
      }
    } else {
      console.warn('⚠️ Skipping Gun worker bridge login because worker bridge is unavailable');
    }
    this.seaPair = pair;
  }

  /**
   * Load or create a SEA keypair, persist as an encrypted custody record, and `gun.user().auth(pair)`.
   * Call after `initialize()`.
   */
  async ensureKeypairAndAuth(): Promise<GunPair> {
    console.log('🔐 Ensuring local SEA identity');
    const SEA = getSEA();
    if (!SEA?.pair) {
      throw new Error('Gun SEA not loaded');
    }
    if (!this.gun) {
      throw new Error('Gun not initialized');
    }

    // K3 (docs/TODO.md): a TechSupport-mode boot (scripts/dev-techsupport-login.js in dev)
    // injects the canonical DM pair under a distinct storage key before this ever runs. Checked
    // first and unconditionally: refuses to start on a key mismatch ("no silent impersonation")
    // rather than falling through to generating an ordinary device identity, and never persists
    // into the ordinary encrypted custody record so the two identities can never merge.
    let techSupportPair: GunPair | null = null;
    try {
      techSupportPair = readBrowserTechSupportRootPair(
        typeof localStorage === 'undefined' ? undefined : localStorage,
      );
    } catch (error) {
      if (error instanceof Error && error.message === TECHSUPPORT_BROWSER_ROOT_DISABLED_ERROR) {
        throw error;
      }
      if (typeof localStorage !== 'undefined' && localStorage.getItem(TECHSUPPORT_KEYPAIR_STORAGE)) {
        // A pair WAS injected but failed validation — this must fail loudly, not fall through
        // to generating a random device identity that would silently impersonate no one but
        // also silently NOT be TechSupport.
        throw error;
      }
    }

    let existingCustody: KeyCustodyRecord | null = null;
    try {
      if (typeof localStorage !== 'undefined') existingCustody = this.readCustodyRecord();
    } catch {
      existingCustody = null;
    }
    let pair: GunPair | null = null;
    let pairStoredInV3 = false;
    const custodyPair = existingCustody ? await this.unwrapKeypairFromStorage(existingCustody) : null;
    const passwordStatus = techSupportPair
      ? { state: 'not-set' as const }
      : await this.getIdentityPasswordProtectionStatus();
    if (techSupportPair) {
      pair = techSupportPair;
      console.log('🔐 Loaded canonical TechSupport DM identity (K3 TechSupport-mode boot)');
    } else if (passwordStatus.state === 'locked') {
      throw new IdentityPasswordRequiredError(passwordStatus.publicIdentity);
    } else {
      const passwordFreeManager = this.getBrowserPasswordFreeCustodyManager();
      if (passwordFreeManager) {
        let hadV3Record = false;
        try {
          hadV3Record = (await passwordFreeManager.getPublicIdentity()) !== null;
          pair = await passwordFreeManager.migrateFrom(this.getLegacyPasswordFreeCustody()) as GunPair | null;
          if (pair) {
            pairStoredInV3 = true;
            console.log(hadV3Record
              ? '🔐 Loaded SEA identity from non-extractable browser custody'
              : '🔐 Migrated SEA identity to non-extractable browser custody');
          } else {
            pair = await this.createFreshPair();
            console.log('🔐 Created new local SEA identity');
            await passwordFreeManager.writeAndVerify(pair as SeaPrivateIdentityMaterial);
            pairStoredInV3 = true;
            console.log('🔐 SEA identity stored with non-extractable browser custody');
          }
        } catch (error) {
          // Once a v3 row exists, corruption or an identity conflict must fail closed. Falling
          // back to another local record here could silently switch the user's identity.
          const recovered = await passwordFreeManager.readPair().catch(() => null);
          if (recovered) {
            if (
              custodyPair &&
              (recovered.pub !== custodyPair.pub || recovered.epub !== custodyPair.epub ||
                recovered.priv !== custodyPair.priv || recovered.epriv !== custodyPair.epriv)
            ) {
              throw error;
            }
            pair = recovered as GunPair;
            pairStoredInV3 = true;
            console.warn('⚠️ Browser custody source cleanup is pending and will resume next startup:', error);
          } else if (hadV3Record || (error instanceof Error && error.message.includes('conflict'))) {
            throw error;
          } else if (passwordFreeManager instanceof NativePasswordFreeCustodyManager) {
            // A native shell advertised an OS-keystore boundary but failed to read/write it.
            // Never downgrade that shell to WebView-local v1 custody: an Android backup restore,
            // damaged ciphertext, unavailable Keychain, or native bridge regression must fail
            // closed instead of silently creating/switching identities in weaker storage.
            throw error;
          } else {
            // Some engines expose IndexedDB/WebCrypto but cannot structured-clone a CryptoKey.
            // Retain the verified v1 path on those engines and never delete its source record.
            console.warn('⚠️ Non-extractable browser custody unavailable — retaining v1 custody:', error);
            this.browserPasswordFreeCustodyManager = null;
            this.identityPasswordManager = null;
          }
        }
      }

      if (!pair) {
        if (custodyPair) {
          pair = custodyPair;
          console.log('🔐 Loaded SEA identity from v1 encrypted custody');
        } else {
          pair = await this.createFreshPair();
          console.log('🔐 Created new local SEA identity');
        }
      }
    }
    if (techSupportPair) {
      console.log('🔐 Skipping ordinary key custody for TechSupport-mode identity');
    } else if (pairStoredInV3) {
      console.log('🔐 SEA identity custody v3 verified');
    } else {
      try {
        await this.persistCustodyRecord(pair!, existingCustody);
        console.log('🔐 SEA identity custody v1 stored');
      } catch (error) {
        console.warn('⚠️ Encrypted SEA key custody unavailable — keeping pair in memory only:', error);
      }
    }

    await this.authenticatePair(pair!);
    // Diagnostic (2026-08-09 real-device investigation): confirm what ensureKeypairAndAuth
    // actually finalized — compare against the heartbeat's own [heartbeat-diag] log to see
    // whether the pair genuinely lacks epub/pub at this point, or getStoredPair() is somehow
    // returning something different/stale by the time the heartbeat later reads it.
    console.log(
      `🔐 [keypair-diag] Local SEA identity ready — hasPub=${!!pair?.pub} hasEpub=${!!pair?.epub} ` +
        `isEmbeddedLocalOrigin=${this.isEmbeddedLocalOrigin()}`,
    );
    return pair!;
  }

  /** Active session pair after `ensureKeypairAndAuth()`. */
  /**
   * Copies the WebView worker's private records to the app's node (OPEN-91 P5): each decrypted by
   * the worker, re-encrypted here, stored on the node unless it already has that record.
   */
  async copyPrivateFromWorker(): Promise<number> {
    if (!this.nativeNode || !this.bridgeReady || !this.seaPair) return 0;
    let copied = 0;
    for (const key of await this.bridge.privateKeys()) {
      if (await this.nativeNode.core({ name: 'private.get', key })) continue;
      const value = await this.bridge.getPrivate(key);
      if (value === null || value === undefined) continue;
      await this.putPrivate(key, value);
      copied += 1;
    }
    return copied;
  }

  /** Shuts the WebView's worker down for good: the app's node holds everything (OPEN-91 P5). */
  retireWorker(): void {
    if (!this.bridgeReady) return;
    this.bridge.terminate();
    this.bridgeReady = false;
  }

  /** A device-local value (the user id and cached-user mirrors): the node's core, else the worker. */
  async getDeviceLocal(path: string): Promise<any> {
    if (this.nativeNode) return this.nativeNode.core({ name: 'device.get', path });
    if (!this.bridgeReady) return null;
    const value = await this.bridge.get(path);
    return value ?? null;
  }

  async putDeviceLocal(path: string, value: unknown): Promise<void> {
    if (this.nativeNode) {
      await this.nativeNode.core({ name: 'device.put', path, value });
      return;
    }
    if (this.bridgeReady) await this.bridge.put(path, value);
  }

  getStoredPair(): GunPair | null {
    return this.seaPair;
  }

  getKeyCustodyStatus(): { active: boolean; publicIdentity?: { pub: string; epub: string }; format?: string } {
    const record = this.readCustodyRecord();
    if (!record) return { active: false };
    return { active: true, publicIdentity: record.publicIdentity, format: record.format };
  }

  exportKeyRecoveryPackage(): string {
    const record = this.readCustodyRecord();
    if (!record) {
      throw new Error('No encrypted key custody record is available to export');
    }
    return JSON.stringify({
      version: 1,
      kind: 'iinpublic-sea-key-recovery',
      warnings: KEY_RECOVERY_WARNINGS,
      custody: record,
    });
  }

  async importKeyRecoveryPackage(raw: string): Promise<{ pub: string; epub: string }> {
    const parsed = JSON.parse(raw) as { custody?: KeyCustodyRecord };
    if (!parsed?.custody) {
      throw new Error('Invalid key recovery package');
    }
    const pair = await this.unwrapKeypairFromStorage(parsed.custody);
    if (!pair) {
      throw new Error('Unable to decrypt imported key recovery package on this device');
    }
    await this.persistCustodyRecord(pair, parsed.custody);
    this.seaPair = pair;
    return toPublicSeaIdentity(pair as SeaPrivateIdentityMaterial);
  }

  /**
   * P2P-X: Run the schema migrator on a Gun-loaded record before returning it
   * to callers.  If the record is already at the current schema version this is
   * a cheap identity pass-through.
   *
   * Usage:
   *   const record = this.migrateOnRead('presence', rawRecord);
   */
  migrateOnRead<T extends Record<string, unknown>>(
    kind: SchemaKind,
    record: T,
  ): T & VersionedRecord {
    return migrateRecord(kind, record);
  }
}
